import { N0DES_API_PREFIX } from "@/api/domain"
import type { Interval, MetricInfo, MetricName, MetricsGroup } from "@/metrics"
import type { MetricNodeInfo, NodeId, ProjectIdParam, Uuid } from "@/types"
import { formatDateWithoutMillsecondsAndZ } from "@/util"
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react"

export type MetricSummaryParams = {
  from: Date // EARLIEST point in time
  to: Date // LATEST point in time
  interval: Interval // interval of time between each data point
}

export type MetricGetParams = {
  group: MetricsGroup
  metric: MetricName // metric
  from: Date // EARLIEST point in time
  to: Date // LATEST point in time
  interval: Interval // interval of time between each data point
  nodeId: NodeId
}

export type MetricNodeParams = {
  group: MetricsGroup
  metric: MetricName // metric
}

export type Aggregation = {
  info: MetricInfo
  data: number[]
}

// API response type from backend
type MetricApiResponse = {
  baseline: number
  buckets: number[]
}

// Define a service using a base URL and expected endpoints
export const metricsApi = createApi({
  reducerPath: "metrics",
  baseQuery: fetchBaseQuery({
    baseUrl: `${N0DES_API_PREFIX}metrics`,
    mode: "cors",
    credentials: "include",
  }),
  endpoints: (build) => ({
    metricSummary: build.query<
      Aggregation[],
      ProjectIdParam & MetricSummaryParams
    >({
      query: ({ project_id, from, to, interval }) => ({
        url: `${project_id}/summary`,
        method: "GET",
        params: {
          from: formatDateWithoutMillsecondsAndZ(from),
          to: formatDateWithoutMillsecondsAndZ(to),
          bucket: interval,
        },
      }),
    }),
    metricGet: build.query<number[], ProjectIdParam & MetricGetParams>({
      query: ({ group, metric, from, to, interval, project_id, nodeId }) => ({
        url: `${project_id}/${group}/${metric}`,
        method: "GET",
        params: {
          from: formatDateWithoutMillsecondsAndZ(from),
          to: formatDateWithoutMillsecondsAndZ(to),
          bucket: interval,
          node: nodeId,
        },
      }),
      transformResponse: (response: MetricApiResponse) => response.buckets,
    }),
    projectMetricGetOnlineNodeCount: build.query<number, ProjectIdParam>({
      query: ({ project_id }) => ({
        url: `project/${project_id}/connected-endpoint-count`,
        method: "GET",
      }),
    }),
    metricNodes: build.query<
      MetricNodeInfo[],
      ProjectIdParam & MetricNodeParams
    >({
      query: ({ group, metric, project_id }) => ({
        url: `${project_id}/${group}/${metric}/nodes`,
        method: "GET",
      }),
    }),
  }),
})

export const {
  useMetricSummaryQuery,
  useMetricGetQuery,
  useMetricNodesQuery,
  useProjectMetricGetOnlineNodeCountQuery,
} = metricsApi
